import { isRecord, requestJson } from "./http.js";

export type ClientType = "web" | "desktop" | "extension" | "other";

/** One signed-in device, as the API lists it. */
export interface DeviceSession {
  readonly id: string;
  readonly clientType: ClientType;
  readonly deviceName: string;
  readonly ip: string;
  readonly createdAt: number;
  readonly lastSeenAt: number;
  /** The session of this very app. */
  readonly current: boolean;
}

const CLIENT_TYPES: ReadonlyArray<ClientType> = ["web", "desktop", "extension", "other"];

export function isDeviceSession(value: unknown): value is DeviceSession {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    CLIENT_TYPES.some((t: ClientType): boolean => t === value["clientType"]) &&
    typeof value["deviceName"] === "string" &&
    typeof value["ip"] === "string" &&
    typeof value["createdAt"] === "number" &&
    typeof value["lastSeenAt"] === "number" &&
    typeof value["current"] === "boolean";
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

function sessionsFrom(body: unknown): DeviceSession[] {
  if (!isRecord(body) || !Array.isArray(body["sessions"])) {
    throw unexpected();
  }
  const out: DeviceSession[] = [];
  for (const entry of body["sessions"]) {
    if (!isDeviceSession(entry)) {
      throw unexpected();
    }
    out.push(entry);
  }
  return out;
}

function okFrom(body: unknown): void {
  if (!isRecord(body) || body["ok"] !== true) {
    throw unexpected();
  }
}

export async function listMySessions(): Promise<DeviceSession[]> {
  return sessionsFrom(await requestJson("/api/v1/account/sessions"));
}

export async function revokeMySession(id: string): Promise<void> {
  okFrom(await requestJson(`/api/v1/account/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }));
}

/** Signs out every other device. Returns how many were signed out. */
export async function revokeOtherSessions(): Promise<number> {
  const body = await requestJson("/api/v1/account/sessions/revoke-others", { method: "POST", body: {} });
  if (!isRecord(body) || body["ok"] !== true || typeof body["ended"] !== "number") {
    throw unexpected();
  }
  return body["ended"];
}

export async function listUserSessions(userId: string): Promise<DeviceSession[]> {
  return sessionsFrom(await requestJson(`/api/v1/admin/users/${encodeURIComponent(userId)}/sessions`));
}

export async function revokeUserSession(userId: string, id: string): Promise<void> {
  okFrom(await requestJson(`/api/v1/admin/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }));
}
